import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchProfileList } from '@/api/auth';
import { useAuth } from '@/store/auth';
import { WEB_ROLES } from '@/api/scope';
import type { Profile } from '@/api/types/common';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';

function profileKey(p: Profile) {
  return `${p.role}:${p.detail?.id ?? p.id}`;
}

export function SwitchAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const current = useAuth((s) => s.profile);
  const setProfile = useAuth((s) => s.setProfile);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchProfileList()
      .then(({ profiles }) => setProfiles(profiles.filter((p) => WEB_ROLES.has(p.role))))
      .catch((err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to load accounts'),
      )
      .finally(() => setLoading(false));
  }, [open]);

  function pick(p: Profile) {
    if (current && profileKey(p) === profileKey(current)) {
      onOpenChange(false);
      return;
    }
    setProfile(p);
    // Branch-scoped data is cached per (admin|manager) scope. Drop everything
    // so the new profile refetches under its own scope.
    queryClient.clear();
    onOpenChange(false);
    navigate('/admin', { replace: true });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Switch account</DialogTitle>
          <DialogDescription>
            Choose which profile to use. You stay signed in.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="py-6 text-center text-sm text-app-hint">Loading…</p>
        ) : !profiles?.length ? (
          <p className="py-6 text-center text-sm text-app-hint">No other accounts found.</p>
        ) : (
          <ul className="space-y-2">
            {profiles.map((p) => {
              const active = current ? profileKey(p) === profileKey(current) : false;
              return (
                <li key={profileKey(p)}>
                  <button
                    onClick={() => pick(p)}
                    className={
                      'card flex w-full items-center justify-between text-left transition hover:bg-app-surface-2' +
                      (active ? ' ring-2 ring-primary' : '')
                    }
                  >
                    <div>
                      <div className="font-semibold capitalize">{p.role}</div>
                      <div className="text-sm text-grey-500">
                        {p.academy_name ?? p.name ?? `Profile #${p.id}`}
                        {p.branch_name ? ` · ${p.branch_name}` : ''}
                      </div>
                    </div>
                    <span className="text-primary">{active ? '✓' : '›'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
